import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { normalizeEntityType } from "../types";
import type { EntityType } from "../types";

export interface EntityNode {
  displayName: string;
  type: EntityType | null;
  parentId: string | null;
}

/**
 * For each given (leaf) entity id, walks the entity `parentId` chain to the
 * root, returning:
 *  - `chains`: entityId → [self, parent, grandparent, …] ids (root last)
 *  - `nodes`:  id → { displayName, type, parentId } for every entity touched
 *
 * Lets callers filter "everything at Magic Kingdom (or Walt Disney World)" by
 * testing whether a selected container id appears in an interaction entity's
 * ancestor chain. Ancestors are fetched once and shared across chains.
 */
export function useEntityHierarchy(entityIds: string[]) {
  const [chains, setChains] = useState<Record<string, string[]>>({});
  const [nodes, setNodes] = useState<Record<string, EntityNode>>({});
  const idsKey = [...new Set(entityIds)].sort().join(",");

  useEffect(() => {
    const ids = [...new Set(entityIds)].filter(Boolean);
    if (ids.length === 0) {
      setChains({});
      setNodes({});
      return;
    }
    let cancelled = false;

    (async () => {
      const nodeMap: Record<string, EntityNode> = {};
      const chainMap: Record<string, string[]> = {};
      // Cache the in-flight promise (not the resolved value) so an entity
      // shared by many chains (a park, a resort) is fetched exactly once even
      // when chains are walked concurrently.
      const cache = new Map<string, Promise<EntityNode | null>>();

      const fetchNode = (id: string): Promise<EntityNode | null> => {
        let pending = cache.get(id);
        if (!pending) {
          pending = getDoc(doc(db, "entities", id))
            .then((snap) => {
              if (!snap.exists()) return null;
              const data = snap.data();
              return {
                displayName: data.displayName ?? "",
                type: normalizeEntityType(data.type),
                parentId: data.parentId ?? null,
              } satisfies EntityNode;
            })
            .catch(() => null);
          cache.set(id, pending);
        }
        return pending;
      };

      // Walk every leaf's chain concurrently; shared ancestors dedupe via cache.
      await Promise.all(
        ids.map(async (leaf) => {
          const chain: string[] = [];
          const seen = new Set<string>();
          let cur: string | null = leaf;
          while (cur && !seen.has(cur)) {
            seen.add(cur);
            const node = await fetchNode(cur);
            if (!node) break;
            nodeMap[cur] = node;
            chain.push(cur);
            cur = node.parentId;
          }
          chainMap[leaf] = chain.length > 0 ? chain : [leaf];
        }),
      );

      if (!cancelled) {
        setChains(chainMap);
        setNodes(nodeMap);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const loading = entityIds.length > 0 && Object.keys(chains).length === 0;
  return { chains, nodes, loading };
}
